import React, { useState } from 'react';
import {
  Box,
  ContentTemplate,
  Progress,
  TextFieldForm,
  yesterdayDate,
  nameRegExp,
} from 'web-library';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { makeStyles } from 'tss-react/mui';
import { UseFormReturn } from 'react-hook-form';
import { stringToSemanticDisplay } from 'web-library/utils/DateUtils';
import { CoordinatorProfileResponseObject } from 'web-library/models/AccountManagementConfiguration';
import ToggleDisplayEditFormItem from '../../../components/ToggleDisplayEditFormItem';
import { useGetIExamMasterCode } from '../../../api-refactored/user/useGetIExamMasterCode';
import { useGetSchoolsByType } from '../../../api-refactored/non-national-exam-registration/useGetSchoolsByType';
import { CreateEditCoordinatorSchemaData } from './CreateEditCoordinatorSchema';
import {LookupListItem} from "../../../api/user/model";
import ControlledDatePicker from "web-library/stories/atom/DatePicker/ControlledDatePicker";
import ControlledAutocomplete from "../../../components/form-items/ControlledAutocomplete";

// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles()((theme) =>
({
  halfWidthLeftItem: {
    width: '50%',
    paddingRight: theme.spacing(2),
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      paddingRight: 0,
      marginBottom: theme.spacing(2),
    },
  },
  formElementContainer: {
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
    width: '100%',
  },
  halfWidthRightItem: {
    width: '50%',
    paddingLeft: theme.spacing(2),
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      paddingLeft: 0,
      marginBottom: theme.spacing(2),
    },
  },
  responsiveFlexContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  keyValueContainer: {
    marginBottom: 0,
  },
}));

export interface CoordinatorPersonalParticularsProps {
  coordinatorProfileDetails?: CoordinatorProfileResponseObject;
  formHook?: UseFormReturn<CreateEditCoordinatorSchemaData>;
  isEditState: boolean;
}

const CoordinatorPersonalParticulars = ({
  coordinatorProfileDetails,
  formHook,
  isEditState,
}: CoordinatorPersonalParticularsProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [dateOfBirth, setDateOfBirth] = useState<string>(
    coordinatorProfileDetails?.dateOfBirth || ''
  );
  const { data: iExamsData } = useGetIExamMasterCode();

  const {
    data: dataOrganisation,
    isLoading: isLoadingOrganisation,
    error: errorOrganisation,
    refetch: refetchOrganisation,
  } = useGetSchoolsByType(true, 'NMOE');

  const organisationDefaultObject =
    dataOrganisation &&
    dataOrganisation.find(
      (obj) => obj.name === coordinatorProfileDetails?.organisation.description
    );

  if (coordinatorProfileDetails?.organisation.code && organisationDefaultObject === undefined) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  return (
    <Box className={classes.responsiveFlexContainer}>
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={t('admin->>UserAccountManagement.Salutation')}
        valueText={coordinatorProfileDetails?.salutation.description}
        containerClassName={classes.formElementContainer}
        EditComponent={
          formHook && (
            <ControlledAutocomplete
              label={t('admin->>UserAccountManagement.Salutation')}
              id="input-select-salutation"
              control={formHook.control}
              name="salutation"
              error={!!formHook.formState.errors?.salutation}
              helperText={formHook.formState.errors?.salutation?.message?.toString()}
              options={iExamsData ? iExamsData.salutation : []}
              defaultValue={coordinatorProfileDetails?.salutation}
              getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              isRequired
              placeholder="Select salutation"
            />
          )
        }
      />
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={t('admin->>UserAccountManagement.CoordinatorName')}
        valueText={coordinatorProfileDetails?.coordinatorName}
        containerClassName={classes.halfWidthLeftItem}
        EditComponent={
          formHook && (
            <TextFieldForm
              form={formHook}
              id="inputText-candidateProfile-coordinatorName"
              label={t('admin->>UserAccountManagement.CoordinatorName')}
              isRequired
              error={!!formHook.formState.errors.coordinatorName}
              helperText={formHook.formState.errors?.coordinatorName?.message?.toString()}
              name="coordinatorName"
              type="text"
              regExpToBlock={nameRegExp}
              defaultValue={coordinatorProfileDetails?.coordinatorName}
              fullWidth
              placeholder="Enter coordinator name"
            />
          )
        }
      />

      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={t('admin->>UserAccountManagement.DateOfBirth')}
        valueText={stringToSemanticDisplay(coordinatorProfileDetails?.dateOfBirth || '')}
        containerClassName={classes.halfWidthRightItem}
        EditComponent={
          formHook && (
            <ControlledDatePicker
              defaultValue={dateOfBirth}
                control={formHook.control}
              label={t('admin->>UserAccountManagement.DateOfBirth')}
              onChange={(date) => {
                setDateOfBirth(date || '');
                formHook.setValue('dateOfBirth', date || '');
              }}
              inputVariant="filled"
              name="dateOfBirth"
              error={!!formHook.formState.errors.dateOfBirth}
              helperText={formHook.formState.errors?.dateOfBirth?.message?.toString()}
              isRequired
              maxDate={dayjs(yesterdayDate)}
              disableFuture
            />
          )
        }
      />

      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={t('admin->>UserAccountManagement.PrivateCentreSchool')}
        valueText={coordinatorProfileDetails?.organisation.description}
        containerClassName={classes.formElementContainer}
        EditComponent={
          formHook && (
            <ControlledAutocomplete<LookupListItem, false, false, undefined>
              label={t('admin->>UserAccountManagement.PrivateCentreSchool')}
              isRequired
              control={formHook.control}
              name="privateCentreSchool"
              id="input-select-privateCentreSchool"
              error={!!formHook.formState.errors.privateCentreSchool}
              helperText={formHook.formState.errors.privateCentreSchool?.message?.toString()}
              loading={isLoadingOrganisation}
              loadingError={!!errorOrganisation}
              loadingErrorOnReFetch={refetchOrganisation}
              options={dataOrganisation || []}
              defaultValue={organisationDefaultObject}
              getOptionLabel={({ name }) => name}
              placeholder="Select private centre/school"
              isOptionEqualToValue={(option, value) => `${option.id}` === `${value.id}`}
            />
          )
        }
      />
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={t('admin->>UserAccountManagement.Designation')}
        valueText={coordinatorProfileDetails?.designation.description}
        containerClassName={classes.formElementContainer}
        EditComponent={
          formHook && (
            <ControlledAutocomplete
              label={t('admin->>UserAccountManagement.Designation')}
              id="input-select-designation"
              control={formHook.control}
              name="designation"
              error={!!formHook.formState.errors.designation}
              helperText={formHook.formState.errors.designation?.message?.toString()}
              options={iExamsData ? iExamsData.designation : []}
              defaultValue={coordinatorProfileDetails?.designation}
              getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              isRequired
              placeholder="Select designation"
            />
          )
        }
      />
    </Box>
  );
};
export default CoordinatorPersonalParticulars;
